import React, { useState } from 'react';
import { Building2, Search, CheckCircle, XCircle, Loader, FileText, MapPin, Award } from 'lucide-react';
import toast from 'react-hot-toast';

export default function QuiFormeTest() {
  const [siret, setSiret] = useState('');
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState(null);

  // Fonction pour formater le SIRET (ajoute des espaces pour la lisibilité)
  const formatSiret = (value) => {
    const numbers = value.replace(/\s/g, '');
    if (numbers.length <= 14) {
      return numbers.replace(/(\d{3})(\d{3})(\d{3})(\d{5})/, '$1 $2 $3 $4').trim();
    }
    return numbers.substring(0, 14);
  };

  // Fonction de test avec un SIRET
  const handleTest = async () => {
    const cleanSiret = siret.replace(/\s/g, '');

    if (!cleanSiret) {
      toast.error('Veuillez entrer un SIRET');
      return;
    }

    if (cleanSiret.length !== 14 || !/^\d{14}$/.test(cleanSiret)) {
      toast.error('Le SIRET doit contenir exactement 14 chiffres');
      return;
    }

    setLoading(true);
    setError(null);
    setResult(null);

    try {
      console.log(`🔍 Test API QuiForme avec SIRET: ${cleanSiret}`);

      const response = await fetch(
        `${import.meta.env.VITE_API_URL}/quiforme/test/${cleanSiret}`,
        {
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('token')}`,
            'Content-Type': 'application/json'
          }
        }
      );

      const data = await response.json();
      console.log('📊 Résultat API QuiForme:', data);

      if (data.success) {
        setResult(data);
        toast.success('Données récupérées avec succès !');
      } else {
        setError(data.error || 'Erreur lors de la récupération des données');
        toast.error(data.error || 'Erreur lors de la récupération des données');
      }
    } catch (err) {
      console.error('❌ Erreur:', err);
      setError('Erreur de connexion au serveur');
      toast.error('Erreur de connexion au serveur');
    } finally {
      setLoading(false);
    }
  };

  // Exemples de SIRET pour tester
  const exampleSirets = [
    { label: 'Exemple 1', siret: '77567933201362' },
    { label: 'Exemple 2', siret: '13002526500013' },
    { label: 'Exemple 3', siret: '42456789012345' }
  ];

  return (
    <div className="min-h-screen bg-gray-50 py-8 px-4">
      <div className="max-w-6xl mx-auto">
        {/* Header */}
        <div className="bg-white rounded-lg shadow-md p-6 mb-6">
          <div className="flex items-center gap-3 mb-4">
            <Building2 className="w-8 h-8 text-blue-600" />
            <h1 className="text-3xl font-bold text-gray-900">Test API QuiForme</h1>
          </div>
          <p className="text-gray-600">
            QuiForme est le référentiel des organismes de formation et centres de formation des apprentis.
            Entrez un SIRET pour tester l'API et voir toutes les informations disponibles.
          </p>
        </div>

        {/* Section de recherche */}
        <div className="bg-white rounded-lg shadow-md p-6 mb-6">
          <h2 className="text-xl font-semibold mb-4 flex items-center gap-2">
            <Search className="w-5 h-5" />
            Rechercher un organisme
          </h2>

          <div className="space-y-4">
            {/* Input SIRET */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                SIRET (14 chiffres)
              </label>
              <div className="flex gap-2">
                <input
                  type="text"
                  value={siret}
                  onChange={(e) => setSiret(formatSiret(e.target.value))}
                  placeholder="123 456 789 01234"
                  className="flex-1 px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  maxLength={17} // 14 chiffres + 3 espaces
                  onKeyPress={(e) => {
                    if (e.key === 'Enter') handleTest();
                  }}
                />
                <button
                  onClick={handleTest}
                  disabled={loading}
                  className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:bg-gray-400 disabled:cursor-not-allowed flex items-center gap-2"
                >
                  {loading ? (
                    <>
                      <Loader className="w-4 h-4 animate-spin" />
                      Chargement...
                    </>
                  ) : (
                    <>
                      <Search className="w-4 h-4" />
                      Rechercher
                    </>
                  )}
                </button>
              </div>
            </div>

            {/* Exemples de SIRET */}
            <div>
              <p className="text-sm text-gray-600 mb-2">Exemples de SIRET pour tester :</p>
              <div className="flex flex-wrap gap-2">
                {exampleSirets.map((example, index) => (
                  <button
                    key={index}
                    onClick={() => setSiret(formatSiret(example.siret))}
                    className="px-3 py-1 bg-gray-100 text-gray-700 rounded-md hover:bg-gray-200 text-sm"
                  >
                    {example.label}: {formatSiret(example.siret)}
                  </button>
                ))}
              </div>
            </div>
          </div>
        </div>

        {/* Message d'erreur */}
        {error && (
          <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-6">
            <div className="flex items-center gap-2 text-red-800">
              <XCircle className="w-5 h-5" />
              <p className="font-medium">{error}</p>
            </div>
          </div>
        )}

        {/* Résultats */}
        {result && result.data && (
          <div className="space-y-6">
            {/* Statistiques */}
            {result.stats && (
              <div className="bg-white rounded-lg shadow-md p-6">
                <h2 className="text-xl font-semibold mb-4 flex items-center gap-2">
                  <CheckCircle className="w-5 h-5 text-green-600" />
                  Résumé
                </h2>
                <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                  <div className="bg-blue-50 p-4 rounded-lg">
                    <p className="text-sm text-gray-600">Qualiopi</p>
                    <p className="text-2xl font-bold text-blue-600">
                      {result.stats.has_qualiopi ? 'Oui' : 'Non'}
                    </p>
                    {result.stats.qualiopi_types && result.stats.qualiopi_types.length > 0 && (
                      <p className="text-xs text-gray-500 mt-1">
                        {result.stats.qualiopi_types.join(', ')}
                      </p>
                    )}
                  </div>
                  <div className="bg-green-50 p-4 rounded-lg">
                    <p className="text-sm text-gray-600">Habilitations</p>
                    <p className="text-2xl font-bold text-green-600">
                      {result.stats.nb_habilitations || 0}
                    </p>
                    <p className="text-xs text-gray-500 mt-1">
                      {result.stats.habilitations_actives || 0} active(s)
                    </p>
                  </div>
                  <div className="bg-purple-50 p-4 rounded-lg">
                    <p className="text-sm text-gray-600">Adresses</p>
                    <p className="text-2xl font-bold text-purple-600">
                      {result.stats.nb_adresses || 0}
                    </p>
                  </div>
                  <div className="bg-orange-50 p-4 rounded-lg">
                    <p className="text-sm text-gray-600">Géolocalisation</p>
                    <p className="text-2xl font-bold text-orange-600">
                      {result.stats.has_geoloc ? 'Oui' : 'Non'}
                    </p>
                  </div>
                </div>
              </div>
            )}

            {/* Informations établissement */}
            {result.data.etablissement && (
              <div className="bg-white rounded-lg shadow-md p-6">
                <h2 className="text-xl font-semibold mb-4 flex items-center gap-2">
                  <Building2 className="w-5 h-5 text-blue-600" />
                  Informations établissement
                </h2>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <InfoField label="SIREN" value={result.data.etablissement.siren} />
                  <InfoField label="Dénomination" value={result.data.etablissement.denomination} />
                  <InfoField label="Enseigne" value={result.data.etablissement.enseigne} />
                  <InfoField label="Code APE" value={result.data.etablissement.code_ape} />
                  <InfoField label="UAI" value={result.data.etablissement.uai} />
                  <InfoField label="SIRET siège" value={result.data.etablissement.siret_siege} />
                  <InfoField label="Date création" value={result.data.etablissement.date_creation} />
                  <InfoField label="État diffusable" value={result.data.etablissement.etat_diffusable ? 'Oui' : 'Non'} />
                </div>
              </div>
            )}

            {/* Unité légale */}
            {result.data.unite_legale && (
              <div className="bg-white rounded-lg shadow-md p-6">
                <h2 className="text-xl font-semibold mb-4 flex items-center gap-2">
                  <FileText className="w-5 h-5 text-green-600" />
                  Unité légale
                </h2>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <InfoField label="Raison sociale" value={result.data.unite_legale.raison_sociale} />
                  <InfoField label="Catégorie juridique" value={result.data.unite_legale.libelle_cat_juridique} />
                  <InfoField label="Code APE" value={result.data.unite_legale.code_ape} />
                  <InfoField label="Sigle" value={result.data.unite_legale.sigle} />
                  <InfoField label="Nom commercial" value={result.data.unite_legale.nom_commercial} />
                  <InfoField label="Multi NDA" value={result.data.unite_legale.multinda ? 'Oui' : 'Non'} />
                </div>
              </div>
            )}

            {/* Adresses */}
            {result.data.adresse_ban && result.data.adresse_ban.length > 0 && (
              <div className="bg-white rounded-lg shadow-md p-6">
                <h2 className="text-xl font-semibold mb-4 flex items-center gap-2">
                  <MapPin className="w-5 h-5 text-red-600" />
                  Adresses (Base Adresse Nationale)
                </h2>
                <div className="space-y-4">
                  {result.data.adresse_ban.map((adresse, index) => (
                    <div key={index} className="border-l-4 border-blue-500 pl-4 py-2">
                      <p className="font-medium text-gray-900">{adresse.adresse_editorialisee}</p>
                      <div className="grid grid-cols-2 md:grid-cols-4 gap-2 mt-2 text-sm text-gray-600">
                        {adresse.commune && <p>Commune: {adresse.commune}</p>}
                        {adresse.departement && <p>Département: {adresse.departement}</p>}
                        {adresse.region && <p>Région: {adresse.region}</p>}
                        {adresse.latitude && adresse.longitude && (
                          <p>GPS: {adresse.latitude}, {adresse.longitude}</p>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* NDA (Numéro déclaration activité) */}
            {result.data.nda && result.data.nda.length > 0 && (
              <div className="bg-white rounded-lg shadow-md p-6">
                <h2 className="text-xl font-semibold mb-4 flex items-center gap-2">
                  <Award className="w-5 h-5 text-yellow-600" />
                  Déclaration d'activité & Qualiopi
                </h2>
                <div className="space-y-4">
                  {result.data.nda.map((nda, index) => (
                    <div key={index} className="bg-gray-50 rounded-lg p-4">
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <InfoField label="NDA" value={nda.nda} />
                        <InfoField label="Dernière déclaration" value={nda.date_derniere_declaration} />
                        <InfoField label="Début exercice" value={nda.debut_exercice} />
                        <InfoField label="Fin exercice" value={nda.fin_exercice} />
                      </div>

                      {/* Qualiopi */}
                      <div className="mt-4">
                        <p className="font-medium text-gray-700 mb-2">Certifications Qualiopi:</p>
                        <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
                          <QualiopiBadge label="Formation" active={nda.qualiopi_action_formation} />
                          <QualiopiBadge label="Bilan compétences" active={nda.qualiopi_bilan_competence} />
                          <QualiopiBadge label="VAE" active={nda.qualiopi_vae} />
                          <QualiopiBadge label="Apprentissage" active={nda.qualiopi_apprentissage} />
                        </div>
                        {nda.obtention_qualiopi && (
                          <p className="text-sm text-gray-600 mt-2">
                            Date obtention: {nda.obtention_qualiopi}
                          </p>
                        )}
                      </div>

                      {/* Spécialités */}
                      {(nda.libelle_specialite_1 || nda.libelle_specialite_2 || nda.libelle_specialite_3) && (
                        <div className="mt-4">
                          <p className="font-medium text-gray-700 mb-2">Spécialités:</p>
                          <ul className="list-disc list-inside space-y-1">
                            {nda.libelle_specialite_1 && (
                              <li className="text-sm text-gray-600">
                                {nda.code_specialite_1} - {nda.libelle_specialite_1}
                              </li>
                            )}
                            {nda.libelle_specialite_2 && (
                              <li className="text-sm text-gray-600">
                                {nda.code_specialite_2} - {nda.libelle_specialite_2}
                              </li>
                            )}
                            {nda.libelle_specialite_3 && (
                              <li className="text-sm text-gray-600">
                                {nda.code_specialite_3} - {nda.libelle_specialite_3}
                              </li>
                            )}
                          </ul>
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Habilitations */}
            {result.data.habilitations && result.data.habilitations.length > 0 && (
              <div className="bg-white rounded-lg shadow-md p-6">
                <h2 className="text-xl font-semibold mb-4">
                  Habilitations RNCP/RS ({result.data.habilitations.length})
                </h2>
                <div className="overflow-x-auto">
                  <table className="min-w-full divide-y divide-gray-200">
                    <thead className="bg-gray-50">
                      <tr>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Code FC</th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Libellé</th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Niveau</th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Statut</th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Rôle</th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Accès</th>
                      </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-200">
                      {result.data.habilitations.map((hab, index) => (
                        <tr key={index} className={!hab.actif ? 'bg-gray-50 opacity-60' : ''}>
                          <td className="px-4 py-3 text-sm font-medium text-gray-900">{hab.code_fc}</td>
                          <td className="px-4 py-3 text-sm text-gray-600">{hab.libelle}</td>
                          <td className="px-4 py-3 text-sm text-gray-600">{hab.niveau}</td>
                          <td className="px-4 py-3">
                            <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                              hab.actif
                                ? 'bg-green-100 text-green-800'
                                : 'bg-gray-100 text-gray-800'
                            }`}>
                              {hab.etat_habilitation}
                            </span>
                          </td>
                          <td className="px-4 py-3 text-sm text-gray-600">
                            {hab.est_certificateur && <span className="mr-1">📜 Certificateur</span>}
                            {hab.est_partenaire && <span className="mr-1">🤝 Partenaire</span>}
                            {hab.a_former && <span className="mr-1">📚 Former</span>}
                            {hab.a_organiser && <span className="mr-1">📋 Organiser</span>}
                          </td>
                          <td className="px-4 py-3 text-xs text-gray-600">
                            <div className="flex flex-wrap gap-1">
                              {hab.accessibilite_fi && <span className="bg-blue-100 text-blue-800 px-1 rounded">FI</span>}
                              {hab.accessibilite_fc && <span className="bg-green-100 text-green-800 px-1 rounded">FC</span>}
                              {hab.accessibilite_ca && <span className="bg-purple-100 text-purple-800 px-1 rounded">CA</span>}
                              {hab.accessibilite_vae && <span className="bg-orange-100 text-orange-800 px-1 rounded">VAE</span>}
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}

            {/* JSON brut */}
            <div className="bg-white rounded-lg shadow-md p-6">
              <h2 className="text-xl font-semibold mb-4">Données JSON brutes</h2>
              <pre className="bg-gray-900 text-green-400 p-4 rounded-lg overflow-auto max-h-96 text-xs">
                {JSON.stringify(result.data, null, 2)}
              </pre>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

// Composant pour afficher un champ d'information
function InfoField({ label, value }) {
  if (!value && value !== 0 && value !== false) return null;

  return (
    <div>
      <p className="text-sm text-gray-500">{label}</p>
      <p className="font-medium text-gray-900">{value}</p>
    </div>
  );
}

// Composant pour les badges Qualiopi
function QualiopiBadge({ label, active }) {
  return (
    <div className={`flex items-center gap-1 px-2 py-1 rounded-md text-sm ${
      active
        ? 'bg-green-100 text-green-800'
        : 'bg-gray-100 text-gray-400'
    }`}>
      {active ? <CheckCircle className="w-3 h-3" /> : <XCircle className="w-3 h-3" />}
      <span>{label}</span>
    </div>
  );
}
